import { useRef, useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useLocation } from 'react-router-dom';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { ReactComponent as ClockSVG } from '../../../../assets/images/home/core-benefits-clock.svg';
import { ReactComponent as LockSVG } from '../../../../assets/images/home/core-benefits-lock.svg';
import { ReactComponent as PaperSVG } from '../../../../assets/images/home/core-benefits-paper.svg';
import { ReactComponent as StarSVG } from '../../../../assets/images/home/core-benefits-star.svg';
import { ReactComponent as TabletSVG } from '../../../../assets/images/home/core-benefits-tablet.svg';
import { ReactComponent as ZapSVG } from '../../../../assets/images/home/core-benefits-zap.svg';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PreFooterBlock from '../../components/PreFooterBlock';
import './styles.scss';
import cx from 'classnames';

const coreBenefitsSteps = [
  {
    iconComponent: <PaperSVG />,
    title: 'Instantly Accessible Records',
  },
  {
    iconComponent: <StarSVG />,
    title: 'Always Survey Ready',
  },
  {
    iconComponent: <ZapSVG />,
    title: 'Faster Documentation Cycles',
  },
  {
    iconComponent: <ClockSVG />,
    title: 'Tailored for Healthcare Standards',
  },
  {
    iconComponent: <TabletSVG />,
    title: 'Mobile & Tablet Friendly',
  },
  {
    iconComponent: <LockSVG />,
    title: 'Secure and Compliant',
  },
];

const CompliancePage = () => {
  const coreBenefitsSwiperRef = useRef();
  const images = [
    { src: '/images/home/compliance-see-in-action-1.png' },
    { src: '/images/home/compliance-see-in-action-2.png' },
    { src: '/images/home/compliance-see-in-action-3.png' },
    { src: '/images/home/compliance-see-in-action-4.png' },
  ];
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [isOpenScheduleDemoModal, setIsOpenScheduleDemoModal] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <main className={cx('home-page compliance-page', { isMobile })}>
      <Header setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal} isOpenScheduleDemoModal={isOpenScheduleDemoModal} />
      <div className="home-page-content">
        <div className="simplify-compliance">
          <div className="simplify-compliance-left">
            <h2 className="simplify-compliance-title">
              Simplify Compliance – <br />
              <span className="blue-part">Be Survey Ready</span>
            </h2>
            <p className="simplify-compliance-subtitle">
              Replace binders and filing cabinets with instantly accessible, survey ready records.
            </p>
            <button className="request-demo-btn" onClick={() => setIsOpenScheduleDemoModal(true)}>
              Request a Demo{' '}
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 9.89771H14.25" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9 4.64771L14.25 9.89771L9 15.1477"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="manual-process">
          <div className="manual-process-left-block">
            <h3 className="say-goodbye-title">Help Your Compliance Team to Keep It Together</h3>
          </div>
          <div className="manual-process-right-block">
            <ul className="manual-process-list">
              <li className="manual-process-list-item">No Delayed documentation and misplaced logs</li>
              <li className="manual-process-list-item">No Paper-based audit trails</li>
              <li className="manual-process-list-item">No Stress before surveys</li>
              <li className="manual-process-list-item">No Lack of remote visibility into compliance</li>
            </ul>
          </div>
        </div>

        <div className="compliance-core-capabilities">
          <h3 className="core-capabilities-title">Our Core Capabilities</h3>

          {isMobile ? (
            <div className="core-capabilities-mobile-wrapper">
              <div className="core-capabilities-row core-capabilities-row-1">
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-clock-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Audit Trails</h4>
                    <p className="core-capabilities-item-subtitle">Quickly retrieve any completed form or log</p>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-danger-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Remote Access</h4>
                    <p className="core-capabilities-item-subtitle">Review, approve, and export from anywhere</p>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-2">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-screenshot-wrapper">
                    <img className="core-capabilities-item-screenshot-img" src="/images/home/customization-screenshot.png" />
                  </div>
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-file-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Inspection Logs</h4>
                    <p className="core-capabilities-item-subtitle">Standardize routine checks for compliance with timestamped forms</p>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-3">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-screenshot-wrapper">
                    <img className="core-capabilities-item-screenshot-img" src="/images/home/core-capabilities-drill-screenshot.png" />
                  </div>
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-fire-drill-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Fire Drill Records</h4>
                    <p className="core-capabilities-item-subtitle">Log events digitally with attendance and signature capture</p>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-4">
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-check-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">POC Documentation</h4>
                    <p className="core-capabilities-item-subtitle">Manage and submit Plans of Correction without paper</p>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-user-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Role-Based Oversight</h4>
                    <p className="core-capabilities-item-subtitle">Give surveyor, admin, or corporate users tailored access</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="core-capabilities-grid-container">
              <div className="core-capabilities-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-clock.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Audit Trails</h4>
                  <p className="core-capabilities-item-subtitle">Quickly retrieve any completed form or log</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-easy-audit">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-danger.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Remote Access</h4>
                  <p className="core-capabilities-item-subtitle">Review, approve, and export from anywhere</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-tall">
                <div>
                  <img
                    className="core-capabilities-item-screenshot-img capabilities-drill-screenshot"
                    src="/images/home/core-capabilities-drill-screenshot.png"
                  />
                </div>
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-device.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Fire Drill Records</h4>
                  <p className="core-capabilities-item-subtitle">Log events digitally with attendance and signature capture</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-wide">
                <div className="core-capabilities-item-screenshot-wrapper">
                  <img className="core-capabilities-item-screenshot-img" src="/images/home/customization-screenshot.png" />
                </div>
                <div className="core-capabilities-item-right-block">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-file.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Inspection Logs</h4>
                    <p className="core-capabilities-item-subtitle">Standardize routine checks for compliance with timestamped forms</p>
                  </div>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-semi-wide">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-clock.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">POC Documentation</h4>
                  <p className="core-capabilities-item-subtitle">Manage and submit Plans of Correction without paper</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-semi-wide">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-user-check.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Role-Based Oversight</h4>
                  <p className="core-capabilities-item-subtitle">Give surveyor, admin, or corporate users tailored access</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="compliance-see-in-action">
          <h3 className="see-in-action-title">See It In Action</h3>
          <div className="see-in-action-images-wrapper">
            {images.map((image, idx) => (
              <div
                className="see-in-action-image-wrapper"
                key={idx}
                onClick={() => {
                  setIndex(idx);
                  setOpen(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                <img className="see-in-action-img" src={image.src} alt={`See in action ${idx + 1}`} />
                <img className="scale-icon" src="/images/home/scale-icon.svg" />
              </div>
            ))}
          </div>

          <Lightbox
            open={open}
            close={() => setOpen(false)}
            index={index}
            slides={images}
            plugins={[Thumbnails]}
            thumbnails={{
              border: 0,
              vignette: false,
              height: 48,
              width: 48,
              padding: 0,
              gap: 14,
              imageFit: 'cover',
            }}
            styles={{
              container: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
              },
              slide: {
                width: '80vw',
                maxWidth: '80vw',
                maxHeight: '75vh',
                height: '75vh',
                margin: 'auto',
                borderRadius: '10px',
              },
            }}
          />
        </div>

        <div className="compliance-core-benefits">
          <h3 className="compliance-core-benefits-title">Core Benefits</h3>
          <div className="compliance-core-benefits-slider">
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={(swiper) => (coreBenefitsSwiperRef.current = swiper)}
              spaceBetween={20}
              loop
              slidesPerView={isMobile ? 1 : 1.5}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2.5 },
                1024: { slidesPerView: 3.2 },
              }}
            >
              {coreBenefitsSteps.map((step, index) => (
                <SwiperSlide key={index}>
                  <div className="compliance-core-benefits-slide">
                    <div style={{ height: '30px' }}></div>
                    <div className="icon-wrapper">{step.iconComponent}</div>
                    <div className="compliance-core-benefits-slide-content">
                      <p className="compliance-core-benefits-title">{step.title}</p>
                      <p className="compliance-core-benefits-subtitle">{step.subtitle}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="custom-nav">
              <button onClick={() => coreBenefitsSwiperRef.current?.slidePrev()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 19.7954L5 12.7954L12 5.79541"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button onClick={() => coreBenefitsSwiperRef.current?.slideNext()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 5.79541L19 12.7954L12 19.7954"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <PreFooterBlock
          title="Stop chasing signatures and digging through folders"
          subtitle="Digitize every compliance process and stay inspection-ready year-round."
          setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal}
        />
      </div>

      <Footer />
    </main>
  );
};

export default CompliancePage;
